/* InputSearchControlRenderer.js */

"use strict";

const _ = require("lodash");

class InputSearchControlRenderer {
    constructor(model) {
        this._model = model;
        this._changeTimeout = undefined;
        this._DEBOUNCE_DURATION = 250;
        this._rootElem = document.createElement("input-search-control");
        this._textField = document.createElement("input");
        this._textField.setAttribute("type", "search");
        this._rootElem.appendChild(this._textField);
        this._rootElem.component = this._model;

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            clearTimeout(this._changeTimeout);
            this._changeTimeout = undefined;
            this._DEBOUNCE_DURATION = undefined;
            this._model = undefined;
            this._rootElem.remove();
            this._textField = undefined;
            this._rootElem = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners() {
        this._model.on("Disabled", (this._onDisabled = this._onDisabled.bind(this)));
        this._model.on("Value", (this._onValue = this._onValue.bind(this)));
        this._model.on("Visible", (this._onVisible = this._onVisible.bind(this)));
        this._model.on("Invalid", (this._onInvalid = this._onInvalid.bind(this)));
        this._model.on("Placeholder", (this._onPlaceholder = this._onPlaceholder.bind(this)));
        this._model.on("DarkTheme", (this._onDarkTheme = this._onDarkTheme.bind(this)));
        this._model.on("Theme", (this._onTheme = this._onTheme.bind(this)));
        this._model.on("Mode", (this._onMode = this._onMode.bind(this)));
        this._model.on("Focus", (this._onFocus = this._onFocus.bind(this)));
        this._model.on("Blur", (this._onBlur = this._onBlur.bind(this)));
        this._model.on("SetAttribute", (this._onSetAttribute = this._onSetAttribute.bind(this)));
        this._textField.addEventListener(
            "input",
            (this._onInputEvent = this._onInputEvent.bind(this))
        );
        this._textField.addEventListener(
            "focus",
            (this._onFocusEvent = this._onFocusEvent.bind(this))
        );
        this._textField.addEventListener(
            "blur",
            (this._onBlurEvent = this._onBlurEvent.bind(this))
        );
        this._textField.addEventListener(
            "keydown",
            (this._onKeyDownEvent = this._onKeyDownEvent.bind(this))
        );
    }
    _unregisterListeners() {
        this._model.removeListener("Disabled", this._onDisabled);
        this._model.removeListener("Value", this._onValue);
        this._model.removeListener("Visible", this._onVisible);
        this._model.removeListener("Invalid", this._onInvalid);
        this._model.removeListener("Placeholder", this._onPlaceholder);
        this._model.removeListener("DarkTheme", this._onDarkTheme);
        this._model.removeListener("Theme", this._onTheme);
        this._model.removeListener("Mode", this._onMode);
        this._model.removeListener("Focus", this._onFocus);
        this._model.removeListener("Blur", this._onBlur);
        this._model.removeListener("SetAttribute", this._onSetAttribute);
        this._textField.removeEventListener("input", this._onInputEvent);
        this._textField.removeEventListener("focus", this._onFocusEvent);
        this._textField.removeEventListener("blur", this._onBlurEvent);
        this._textField.removeEventListener("keydown", this._onKeyDownEvent);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._setDisabled(this._model.disabled);
            this._setValue(this._model.value);
            this._setVisible(this._model.visible);
            this._setInvalid(this._model.invalid);
            this._setPlaceholder(this._model.placeholder);
            this._setDarkTheme(this._model.darkTheme);
            this._setTheme(this._model.theme);
            this._setMode(this._model.mode);
            resolve();
        });
        return prom;
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the disabled state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDisabled(newVal) {
        this._setDisabled(newVal);
    }

    /**
     * Fired when the invalid state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onInvalid(newVal) {
        this._setInvalid(newVal);
    }

    /**
     * Fired when the value has been updated in the model
     * @param newVal {String}
     * @param oldVal {String}
     * @private
     */
    _onValue(newVal /*, oldVal*/) {
        this._setValue(newVal);
    }

    /**
     * Fired when the visible state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onVisible(newVal) {
        this._setVisible(newVal);
    }

    /**
     * Fired when the placeholder has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onPlaceholder(newVal) {
        this._setPlaceholder(newVal);
    }

    /**
     * Fired when the darkTheme has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDarkTheme(newVal) {
        this._setDarkTheme(newVal);
    }

    /**
     * Fired when the theme has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onTheme(newVal, oldVal) {
        this._setTheme(newVal, oldVal);
    }

    /**
     * Fired when the mode has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onMode(newVal) {
        this._setMode(newVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus() {
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur() {
        this._setFocus(false);
    }

    /**
     * Fired when setAttribute has been called in the model
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _onSetAttribute(attrName, attrValue) {
        this._setAttribute(attrName, attrValue);
    }

    /**
     * Update the value in the model based on user input
     * @param event {Event}
     * @private
     */
    _onInputEvent(event) {
        let oldVal = _.cloneDeep(this._model.value);
        let newVal = this._textField.value;
        this._model.setValue(this._textField.value, true);
        this._model._triggerUserValue(newVal, oldVal, event);
        clearTimeout(this._changeTimeout);
        this._changeTimeout = setTimeout(() => {
            this._model._triggerUserChange(this._model.value);
        }, this._DEBOUNCE_DURATION);
    }

    /**
     * Pass the focus event up to the model
     * @param event {Event}
     * @private
     */
    _onFocusEvent(event) {
        this._model._triggerUserFocus(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onBlurEvent(event) {
        this._model._triggerUserBlur(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onKeyDownEvent(event) {
        this._model._triggerUserKeyDown(event);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Toggle the disabled state
     * @param newVal {Boolean}
     * @private
     */
    _setDisabled(newVal) {
        if (newVal === true) {
            this._textField.disabled = true;
        } else {
            this._textField.disabled = false;
        }
    }

    /**
     * Toggle the invalid state
     * @param newVal {Boolean}
     * @private
     */
    _setInvalid(newVal) {
        if (newVal === true) {
            this._textField.classList.add("invalid");
        } else {
            this._textField.classList.remove("invalid");
        }
    }

    /**
     * Put the placeholder text in place and move the cursor to the front of the selection if placeholder text
     * is displaying.
     * @param newVal {String}
     * @private
     */
    _setPlaceholder(newVal) {
        if (newVal !== undefined && newVal !== null && newVal !== "") {
            this._textField.placeholder = newVal;
        } else {
            this._textField.placeholder = "";
        }
    }

    /**
     * Toggle the display text value of the button
     * @param newVal {String}
     * @private
     */
    _setValue(newVal) {
        if (newVal !== this._textField.value) {
            if (newVal !== undefined && newVal !== null && newVal !== "") {
                this._textField.value = newVal;
            } else {
                this._textField.value = "";
            }
        }
    }

    /**
     * Toggle the visible state
     * @param newVal {Boolean}
     * @private
     */
    _setVisible(newVal) {
        if (newVal === true) {
            this._rootElem.classList.remove("hidden");
        } else {
            this._rootElem.classList.add("hidden");
        }
    }

    /**
     * Toggle or set the dark theme
     * @param newVal {Boolean}
     * @private
     */
    _setDarkTheme(newVal) {
        if (newVal === true) {
            this._rootElem.classList.add("dark");
        } else {
            this._rootElem.classList.remove("dark");
        }
    }

    /**
     * Set the theme name
     * @param newVal {String}
     * @private
     */
    _setTheme(newVal, oldVal) {
        if (oldVal) {
            this._rootElem.classList.remove(oldVal);
        }
        if (newVal) {
            this._rootElem.classList.add(newVal);
        }
    }

    /**
     * Set the filter or search mode
     * @param newVal {String}
     * @private
     */
    _setMode(newVal) {
        if (newVal.toLowerCase() === "filter") {
            this._rootElem.classList.add("filter");
        } else {
            this._rootElem.classList.remove("filter");
        }
    }

    /**
     * Set or remove focus on the textfield
     * @param newVal {Boolean}
     * @private
     */
    _setFocus(newVal) {
        if (newVal === true) {
            this._textField.focus();
        } else {
            this._textField.blur();
        }
    }

    /**
     * Set an attribute on the native control
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _setAttribute(attrName, attrValue) {
        if (!_.isUndefined(attrValue)) {
            this._textField.setAttribute(attrName, attrValue);
        } else {
            this._textField.removeAttribute(attrName);
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = InputSearchControlRenderer;
